import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Check, CheckCheck, X, Circle } from 'lucide-react'
import Button from '../Button'
import { designTokens } from '@/utils/design-tokens'
import { useNotificationWebSocket } from '@/hooks/useNotificationWebSocket'
import { NotificationService } from '@/api/generated/services/NotificationService'
import type { NotificationResponse } from '@/api/generated/models/NotificationResponse'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectWorkspaceAndFetchOrgMember } from '@/store/workspaceSlice'
import { useAlert } from '@/components/ui/Alert/exports'

interface NotificationButtonProps {
  className?: string
}

const NotificationButton: React.FC<NotificationButtonProps> = ({ className = '' }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { showError, showInfo } = useAlert()
  // 알림 클릭 시 다른 워크스페이스로 자동 전환하기 위해 Redux의 workspaces / selectedWorkspace 참조
  const { workspaces, selectedWorkspace } = useAppSelector(state => state.workspace)

  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<NotificationResponse[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const dropdownRef = useRef<HTMLDivElement>(null)

  // WebSocket 연결
  const {
    connectionStatus,
    unreadCount,
    setUnreadCount,
  } = useNotificationWebSocket({
    enabled: true,
    onNotification: (notification) => {
      // 새 알림이 오면 목록 맨 앞에 추가
      setNotifications(prev => [{
        id: notification.id,
        account_id: '',
        notification_type: notification.notification_type,
        notification_type_display: notification.notification_type_display || notification.notification_type,
        message: notification.message,
        extra_data: notification.extra_data,
        is_read: notification.is_read,
        created_at: notification.created_at,
      }, ...prev])
    },
  })

  // 알림 목록 및 읽지 않은 개수 조회
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const [notificationsRes, unreadRes] = await Promise.all([
        NotificationService.appsNotificationViewsGetMyNotifications({
          page: 1,
          perPage: 50,
        }),
        NotificationService.appsNotificationViewsGetUnreadCount(),
      ])

      if (notificationsRes?.data) {
        setNotifications(notificationsRes.data)
      }

      if (unreadRes?.count !== undefined) {
        setUnreadCount(unreadRes.count)
      }
    } catch (err) {
      console.error('알림 목록 조회 실패:', err)
      setError('알림을 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [setUnreadCount])

  // 드롭다운 열릴 때 알림 목록 조회
  useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    } else {
      setSelectedIds(new Set())
    }
  }, [isOpen, fetchNotifications])

  // 초기 읽지 않은 개수 조회
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await NotificationService.appsNotificationViewsGetUnreadCount()
        if (res?.count !== undefined) {
          setUnreadCount(res.count)
        }
      } catch (err) {
        console.error('읽지 않은 알림 개수 조회 실패:', err)
      }
    }

    fetchUnreadCount()
  }, [setUnreadCount])

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // 선택 읽음 처리
  const handleMarkSelectedAsRead = async () => {
    if (selectedIds.size === 0) return

    try {
      await NotificationService.appsNotificationViewsMarkNotificationsRead({
        requestBody: { notification_ids: Array.from(selectedIds) },
      })

      // 로컬 상태 업데이트
      setNotifications(prev =>
        prev.map(n => selectedIds.has(n.id) ? { ...n, is_read: true } : n)
      )

      // 읽지 않은 개수 업데이트
      const markedUnreadCount = notifications.filter(n => selectedIds.has(n.id) && !n.is_read).length
      setUnreadCount(prev => Math.max(0, prev - markedUnreadCount))

      setSelectedIds(new Set())
    } catch (err) {
      console.error('선택 읽음 처리 실패:', err)
    }
  }

  // 전체 읽음 처리
  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.appsNotificationViewsMarkAllNotificationsRead()

      // 로컬 상태 업데이트
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
      setSelectedIds(new Set())
    } catch (err) {
      console.error('전체 읽음 처리 실패:', err)
    }
  }

  // 단일 알림 읽음 처리
  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await NotificationService.appsNotificationViewsMarkNotificationsRead({
        requestBody: { notification_ids: [notificationId] },
      })

      // 로컬 상태 업데이트
      const notification = notifications.find(n => n.id === notificationId)
      if (notification && !notification.is_read) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        )
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (err) {
      console.error('읽음 처리 실패:', err)
    }
  }

  // 선택 토글
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // 알림 유형별 이동 경로 매핑
  const getNotificationRoute = (notification: NotificationResponse): string | null => {
    // AI 분석 작업(Job) 종료 알림 — extra_data.job_id가 있으면 보고서 관리 화면으로 이동
    if (notification.extra_data?.job_id) {
      return '/project/report'
    }

    switch (notification.notification_type) {
      case 'task_created':
      case 'task_update':
      case 'task_item_assignee_approved':
      case 'task_item_manager_approved':
        return '/project/task'
      default:
        return null
    }
  }

  // 알림이 클릭 가능한지 여부
  const isNotificationClickable = (notification: NotificationResponse): boolean => {
    return getNotificationRoute(notification) !== null
  }

  // 알림 클릭 시 유형별 화면 이동.
  // 알림이 다른 워크스페이스 소속이면 먼저 워크스페이스를 전환한 뒤 이동해야
  // 해당 항목이 화면에 표시되어 하이라이트(붉은 깜빡임)가 동작한다.
  const handleNotificationClick = async (notification: NotificationResponse) => {
    const route = getNotificationRoute(notification)
    if (!route) return

    // 1) 워크스페이스 전환 필요 여부 판단
    const targetWorkspaceId = notification.workspace_id
    const needSwitch =
      !!targetWorkspaceId && targetWorkspaceId !== selectedWorkspace?.id

    if (needSwitch) {
      const targetWorkspace = workspaces.find(w => w.id === targetWorkspaceId)
      if (!targetWorkspace) {
        // 사용자가 참여하지 않은 워크스페이스(권한 없음) → 안내만 표시
        showError(
          `이 알림은 "${notification.workspace_name ?? '다른 워크스페이스'}"에 속해 있어 접근할 수 없습니다.`,
        )
        return
      }
      try {
        showInfo(`"${targetWorkspace.name}" 워크스페이스로 전환 중...`)
        const result = await dispatch(selectWorkspaceAndFetchOrgMember(targetWorkspace))
        if (!selectWorkspaceAndFetchOrgMember.fulfilled.match(result)) {
          showError('워크스페이스 전환에 실패했습니다.')
          return
        }
      } catch (err) {
        console.error('Failed to switch workspace from notification:', err)
        showError('워크스페이스 전환 중 오류가 발생했습니다.')
        return
      }
    }

    // 2) 읽지 않은 알림이면 읽음 처리 (워크스페이스 전환 후에 실행해도 됨)
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id)
    }

    // 3) 라우트 이동 — TaskManagement 페이지의 useEffect가 state를 받아
    //    데이터 로드 완료 후 스크롤 + 깜빡임 하이라이트 처리
    navigate(route, { state: { from: 'notification', ...notification.extra_data } })
    setIsOpen(false)
  }

  // 날짜 포맷팅
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffMins < 1) return '방금 전'
    if (diffMins < 60) return `${diffMins}분 전`
    if (diffHours < 24) return `${diffHours}시간 전`
    if (diffDays < 7) return `${diffDays}일 전`

    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  // 연결 상태 색상
  const getConnectionColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return designTokens.colors.success.DEFAULT
      case 'connecting':
        return designTokens.colors.warning.DEFAULT
      case 'error':
      case 'disconnected':
        return designTokens.colors.error.DEFAULT
      default:
        return designTokens.colors.gray[4]
    }
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* 알림 버튼 */}
      <Button
        size="medium"
        color="white"
        onClick={() => setIsOpen(!isOpen)}
        className="h-10 px-2 sm:px-[10px] gap-1 border-2 relative"
        style={{ borderColor: designTokens.colors.gray[1] }}
        title="알림"
      >
        <Bell className="w-[15px] h-[13.5px]" strokeWidth={2} />
        <span
          className="hidden md:inline text-lg font-semibold"
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            color: designTokens.colors.gray[6],
          }}
        >
          알림
        </span>

        {/* 읽지 않은 알림 배지 */}
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold rounded-full"
            style={{
              backgroundColor: designTokens.colors.error.DEFAULT,
              color: designTokens.colors.white,
              fontFamily: designTokens.typography.fontFamily.sans[0],
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}

        {/* 연결 상태 표시 */}
        <span
          className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white"
          style={{ backgroundColor: getConnectionColor() }}
          title={
            connectionStatus === 'connected' ? '연결됨' :
            connectionStatus === 'connecting' ? '연결 중...' :
            connectionStatus === 'error' ? '연결 오류' : '연결 끊김'
          }
        />
      </Button>

      {/* 드롭다운 패널 */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-lg shadow-lg border z-50"
          style={{ borderColor: designTokens.colors.gray[1] }}
        >
          {/* 헤더 */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderBottomColor: designTokens.colors.gray[1] }}
          >
            <div className="flex items-center gap-2">
              <h3
                className="text-base font-semibold"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  color: designTokens.colors.gray[6],
                }}
              >
                알림
              </h3>
              {unreadCount > 0 && (
                <span
                  className="px-2 py-0.5 text-xs font-medium rounded-full"
                  style={{
                    backgroundColor: designTokens.colors.error.DEFAULT,
                    color: designTokens.colors.white,
                  }}
                >
                  {unreadCount}개 안읽음
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {selectedIds.size > 0 && (
                <button
                  onClick={handleMarkSelectedAsRead}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="선택 읽음 처리"
                >
                  <Check className="w-4 h-4" style={{ color: designTokens.colors.gray[5] }} />
                </button>
              )}
              <button
                onClick={handleMarkAllAsRead}
                className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                title="전체 읽음 처리"
              >
                <CheckCheck className="w-4 h-4" style={{ color: designTokens.colors.gray[5] }} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                title="닫기"
              >
                <X className="w-4 h-4" style={{ color: designTokens.colors.gray[5] }} />
              </button>
            </div>
          </div>

          {/* 알림 목록 */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div
                  className="text-sm"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.gray[4],
                  }}
                >
                  로딩 중...
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <div
                  className="text-sm"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.error.DEFAULT,
                  }}
                >
                  {error}
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <Bell
                  className="w-10 h-10"
                  style={{ color: designTokens.colors.gray[2] }}
                />
                <div
                  className="text-sm"
                  style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    color: designTokens.colors.gray[4],
                  }}
                >
                  알림이 없습니다
                </div>
              </div>
            ) : (
              notifications.map((notification) => {
                const clickable = isNotificationClickable(notification)
                return (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b transition-colors ${
                    selectedIds.has(notification.id) ? 'bg-blue-50' : clickable ? 'hover:bg-gray-50' : ''
                  }`}
                  style={{
                    borderBottomColor: designTokens.colors.gray[0],
                    backgroundColor: !notification.is_read && !selectedIds.has(notification.id)
                      ? 'rgba(108, 172, 228, 0.08)'
                      : undefined,
                    cursor: clickable ? 'pointer' : 'default',
                  }}
                  onClick={() => clickable && handleNotificationClick(notification)}
                >
                  {/* 선택 체크박스 */}
                  <button
                    className="flex-shrink-0 mt-1 p-0 border-0 bg-transparent cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleSelection(notification.id)
                    }}
                    title="선택"
                  >
                    {selectedIds.has(notification.id) ? (
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: designTokens.colors.secondary.DEFAULT }}
                      >
                        <Check className="w-3 h-3" style={{ color: designTokens.colors.white }} />
                      </div>
                    ) : (
                      <Circle
                        className="w-4 h-4"
                        fill={notification.is_read ? 'transparent' : designTokens.colors.secondary.DEFAULT}
                        strokeWidth={notification.is_read ? 1 : 0}
                        style={{
                          color: notification.is_read
                            ? designTokens.colors.gray[2]
                            : designTokens.colors.secondary.DEFAULT,
                        }}
                      />
                    )}
                  </button>

                  {/* 알림 내용 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-xs px-1.5 py-0.5 rounded"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          backgroundColor: designTokens.colors.gray[0],
                          color: designTokens.colors.gray[5],
                        }}
                      >
                        {notification.notification_type_display}
                      </span>
                      <span
                        className="text-xs"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          color: designTokens.colors.gray[4],
                        }}
                      >
                        {formatDateTime(notification.created_at)}
                      </span>
                    </div>
                    <p
                      className="text-sm"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        color: notification.is_read
                          ? designTokens.colors.gray[4]
                          : designTokens.colors.gray[6],
                        fontWeight: notification.is_read ? 400 : 500,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                      title={notification.message}
                    >
                      {notification.message}
                    </p>
                  </div>

                  {/* 읽음 처리 버튼 */}
                  {!notification.is_read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMarkAsRead(notification.id)
                      }}
                      className="flex-shrink-0 p-1 rounded hover:bg-gray-200 transition-colors"
                      title="읽음 처리"
                    >
                      <Check
                        className="w-3.5 h-3.5"
                        style={{ color: designTokens.colors.gray[4] }}
                      />
                    </button>
                  )}
                </div>
                )
              })
            )}
          </div>

          {/* 푸터 - 연결 상태 */}
          <div
            className="flex items-center justify-center gap-2 px-4 py-2 border-t"
            style={{ borderTopColor: designTokens.colors.gray[1] }}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: getConnectionColor() }}
            />
            <span
              className="text-xs"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                color: designTokens.colors.gray[4],
              }}
            >
              {connectionStatus === 'connected' && '연결됨'}
              {connectionStatus === 'connecting' && '연결 중...'}
              {connectionStatus === 'disconnected' && '연결 끊김'}
              {connectionStatus === 'error' && '연결 오류'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationButton
